import React from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentSubscription, SUBSCRIPTION_STATUS } from '../services/shared/subscriptionService';
import './landing.css';


function LandingModules({ modules }) {
  const icons = {
    'Trust Signals': 'bi-shield-check',
    'Operations Mgmt': 'bi-gear-wide-connected',
    'Supplier Ecosystem': 'bi-diagram-3',
    'Data Analytics': 'bi-bar-chart-line',
    'Investor Market': 'bi-graph-up-arrow',
    'Trust Tech Scan': 'bi-search',
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
      {modules.map((mod, i) => (
        <div key={i} className="lp-card rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 sm:p-6 hover:bg-white/[0.06] hover:border-purple-500/20">
          <div className="flex items-center gap-3 mb-2 sm:mb-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
              <i className={`bi ${icons[mod.title] || 'bi-box'} text-purple-400`} style={{ fontSize: '1rem' }}></i>
            </div>
            <h3 className="font-semibold text-[0.85rem] sm:text-[0.95rem] text-white">{mod.title}</h3>
          </div>
          <div className="text-sm text-purple-200 font-medium pl-11 sm:pl-12">{mod.metric}</div>
        </div>
      ))}
    </div>
  );
}

function LandingIntegrations({ items }) {
  const icons = {
    'Slack': 'bi-slack', 'Teams': 'bi-microsoft-teams', 'ServiceNow': 'bi-cloud-check',
    'Snowflake': 'bi-snow', 'Azure': 'bi-cloud-fill', 'Canton Network': 'bi-lightning-charge-fill',
  };
  return (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4 justify-center">
      {items.map((item, i) => (
        <div key={i} className="lp-card px-3 sm:px-5 py-2.5 sm:py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs sm:text-sm font-medium hover:bg-white/[0.06] hover:border-purple-500/15 flex items-center justify-center sm:justify-start gap-2">
          <i className={`bi ${icons[item] || 'bi-plug'} text-purple-400/70`} style={{ fontSize: '0.9rem' }}></i>
          <span className="text-slate-300">{item}</span>
        </div>
      ))}
    </div>
  );
}

// Background & content data
const bgImage = process.env.PUBLIC_URL + '/landingpage.png';
const pillars = [
  { title: 'Easy to Read Graphs', desc: 'Unify fragmented trust signals into one continuously enriched layer.', icon: 'bi-bar-chart-line-fill' },
  { title: 'Risk Management & Governance', desc: 'Automate controls & policies that evolve with changing risk surfaces.', icon: 'bi-shield-lock-fill' },
  { title: 'ESG Data Intelligence', desc: 'Correlate behavioral, operational & ecosystem signals in real time.', icon: 'bi-cpu-fill' },
  { title: 'Shareholder Credibility', desc: 'Operationalize sustainable growth with measurable integrity outcomes.', icon: 'bi-award-fill' }
];
const modules = [
  { title: 'Trust Signals', metric: '82% Health', path: '/signal' },
  { title: 'Operations Mgmt', metric: '6 Active Policies', path: '/operations-management' },
  { title: 'Supplier Ecosystem', metric: '3 New Vendors', path: '/supplier-ecosystem' },
  { title: 'Data Analytics', metric: '12 Reports', path: '/data-analytics' },
  { title: 'Investor Market', metric: '4 KPIs ↑', path: '/investor-market' },
  { title: 'Trust Tech Scan', metric: 'Last scan 2h ago', path: '/trust-tech-scan' },
];
const flowItems = ['Identity','Data Lineage','Signals','Controls','Reports','Ethics','Insights'];

export default function LandingPage(){
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [subscription, setSubscription] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [showKroPopup, setShowKroPopup] = React.useState(false);
  const [showB2allPopup, setShowB2allPopup] = React.useState(false);
  const [showSollomonPopup, setShowSollomonPopup] = React.useState(false);

  // Integrity Benchmark lead-capture form state
  const [benchmarkEmail, setBenchmarkEmail] = React.useState('');
  const [benchmarkStatus, setBenchmarkStatus] = React.useState('idle'); // idle | submitting | success | error

  const handleBenchmarkSubmit = async (e) => {
    e.preventDefault();
    if (!benchmarkEmail || benchmarkStatus === 'submitting') return;
    setBenchmarkStatus('submitting');
    try {
      const API_BASE = (process.env.REACT_APP_KRO_API_BASE || 'https://api.kromarketplace.com').replace(/\/api\/v1$/, '');
      const res = await fetch(`${API_BASE}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Integrity Benchmark Request',
          email: benchmarkEmail,
          inquiryType: 'esg',
          subject: 'Integrity Benchmark Report Request',
          message: `Requested the Integrity Benchmark sample report via the KRO Studio landing page. Contact email: ${benchmarkEmail}.`,
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setBenchmarkStatus('success');
      setBenchmarkEmail('');
    } catch (err) {
      setBenchmarkStatus('error');
    }
  };

  // Close the B2ALL modal and smooth-scroll to the matching on-page section anchor.
  const scrollToSection = (id) => {
    setShowB2allPopup(false);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const login = ()=> instance.loginRedirect();

  // Check authentication and subscription status
  React.useEffect(() => {
    const account = instance.getActiveAccount?.();
    setIsAuthenticated(!!account);
    
    if (account) {
      const currentSub = getCurrentSubscription();
      setSubscription(currentSub);
    }
  }, [instance]);

  // Handle launch button click
  const handleLaunch = () => {
    if (!isAuthenticated) {
      // Not logged in - redirect to login
      login();
      return;
    }

    // Check subscription status
    if (!subscription || 
        subscription.status === SUBSCRIPTION_STATUS.EXPIRED || 
        subscription.status === SUBSCRIPTION_STATUS.CANCELLED) {
      // No active subscription - go to subscription page
      navigate('/subscription');
    } else {
      // Has active subscription - go to dashboard
      navigate('/trust-tech-studio');
    }
  };

  // Personalization (basic): derive role from ID token claims if available
  const account = instance.getActiveAccount?.();
  const rawName = account?.name || '';
  const rawRoles = account?.idTokenClaims?.roles || account?.idTokenClaims?.role || [];
  const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles].filter(Boolean);
  const roleLabel = roles[0] || 'engaged';
  const personalizedSub = `Built to code K-R-O's relational design standard™ into strategy, technology, and operations across the market, accelerating ethical outcomes with confidence.`;
  
  // Determine button text based on state
  const getButtonText = () => {
    if (!isAuthenticated) {
      return 'Launch Trust Tech Studio™';
    }
    if (!subscription || 
        subscription.status === SUBSCRIPTION_STATUS.EXPIRED || 
        subscription.status === SUBSCRIPTION_STATUS.CANCELLED) {
      return 'Subscribe to Launch Trust Tech Studio™';
    }
    return 'Launch Trust Tech Studio™';
  };

  // Intersection Observer for reveal animations
  React.useEffect(()=>{
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if(prefersReduced) return; // skip animations
    const els = document.querySelectorAll('[data-reveal]');
    const obs = new IntersectionObserver((entries)=>{
      entries.forEach(en=>{
        if(en.isIntersecting){
          en.target.classList.add('reveal-in');
          obs.unobserve(en.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach(el=> obs.observe(el));
    return ()=> obs.disconnect();
  }, []);

  // Defer adding a class to body to signal hydration for potential future effects
  React.useEffect(()=>{
    requestAnimationFrame(()=> document.body.classList.add('lp-ready'));
  }, []);

  return (
  <div className="min-h-screen w-full bg-[#0c0a15] text-white font-sans overflow-x-hidden">
      {/* Hero */}
      <section className="relative w-full min-h-[80vh] sm:min-h-screen overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src={bgImage}
            alt="Mountain landscape"
            className="w-full h-full object-cover"
            style={{ opacity: 0.18, filter: 'blur(2px)' }}
            fetchpriority="high"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(165deg, rgba(12,10,21,0.97) 0%, rgba(20,10,35,0.92) 40%, rgba(12,10,21,0.96) 100%)' }} />
        {/* Top bar — mobile + desktop */}
        <div className="relative z-20 w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
          {/* Mobile top bar — single row */}
          <div className="flex lg:hidden items-center justify-between gap-1.5">
            <button
              type="button"
              onClick={() => setShowB2allPopup(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-purple-400/20 bg-purple-500/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
              aria-haspopup="dialog"
              title="Business to All — one operating system, five doors"
            >
              <span className="text-[0.6rem] font-semibold text-purple-200">OS Identity: B2ALL</span>
              <i className="bi bi-grid-3x3-gap-fill text-purple-300/70" style={{ fontSize: '0.55rem' }}></i>
            </button>
            <div className="flex items-center gap-1.5 text-[0.55rem] font-medium text-purple-300/70">
              <span>RWA</span>
              <svg width="40" height="16" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 32 Q30 4 60 16 Q90 4 115 32" stroke="#a78bfa" strokeWidth="3" fill="none" strokeLinecap="round" />
                <circle cx="60" cy="18" r="4" fill="#c084fc" opacity="0.8">
                  <animate attributeName="cx" values="20;100" dur="3s" repeatCount="indefinite" />
                </circle>
              </svg>
              <span>On-Chain</span>
            </div>
            <a href="https://www.kromarketplace.com/launch-portal" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-purple-400/20 bg-purple-500/10 text-[0.6rem] font-semibold text-purple-200">
              <i className="bi bi-rocket-takeoff-fill"></i> Launch Pad
            </a>
          </div>
          {/* Desktop top bar */}
          <div className="hidden lg:flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-purple-200 tracking-wide">Real-World Assets</span>
              <svg width="120" height="40" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 32 Q30 4 60 16 Q90 4 115 32" stroke="url(#bridgeGrad)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                <line x1="5" y1="32" x2="115" y2="32" stroke="#7c3aed" strokeWidth="1.5" opacity="0.4" />
                <line x1="30" y1="18" x2="30" y2="32" stroke="#7c3aed" strokeWidth="1.5" opacity="0.3" />
                <line x1="90" y1="18" x2="90" y2="32" stroke="#7c3aed" strokeWidth="1.5" opacity="0.3" />
                <circle cx="35" cy="24" r="4" fill="#a78bfa" opacity="0.9">
                  <animate attributeName="cx" values="20;100" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.9;0.6;0.9" dur="3s" repeatCount="indefinite" />
                </circle>
                <circle cx="55" cy="20" r="3" fill="#c084fc" opacity="0.7">
                  <animate attributeName="cx" values="30;110" dur="3.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.7;0.4;0.7" dur="3.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="75" cy="22" r="3.5" fill="#e9d5ff" opacity="0.8">
                  <animate attributeName="cx" values="10;95" dur="2.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0.5;0.8" dur="2.8s" repeatCount="indefinite" />
                </circle>
                <defs>
                  <linearGradient id="bridgeGrad" x1="5" y1="20" x2="115" y2="20" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#a78bfa" />
                    <stop offset="50%" stopColor="#c084fc" />
                    <stop offset="100%" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="text-sm font-semibold text-purple-200 tracking-wide">On-Chain Settlement</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setShowB2allPopup(true)}
                className="flex px-5 py-2.5 rounded-xl border border-purple-400/25 bg-purple-500/10 text-sm font-semibold text-purple-200 items-center gap-2 hover:bg-purple-500/20 hover:border-purple-400/40 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
                aria-haspopup="dialog"
                title="Business to All — one operating system, five doors"
              >
                OS Identity: B2ALL · Compliant by Design
                <i className="bi bi-grid-3x3-gap-fill" style={{ fontSize: '0.75rem', opacity: 0.6 }}></i>
              </button>
              <a href="https://www.kromarketplace.com/register-trial" target="_blank" rel="noopener noreferrer"
                className="flex px-5 py-2.5 rounded-xl border border-purple-400/25 bg-purple-500/10 text-sm font-semibold text-purple-200 hover:bg-purple-500/20 hover:border-purple-400/40 transition-all items-center gap-2">
                <i className="bi bi-rocket-takeoff-fill" style={{ fontSize: '0.9rem' }}></i>
                Launch Pad
                <i className="bi bi-arrow-up-right" style={{ fontSize: '0.7rem', opacity: 0.6 }}></i>
              </a>
            </div>
          </div>
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start sm:items-center" style={{ minHeight: 'calc(100vh - 60px)' }}>
          <div
            className="w-full flex flex-col lg:flex-row items-center gap-6 sm:gap-10 lg:gap-16"
            style={{ paddingTop: 'clamp(0.75rem, 3vh, 3rem)', paddingBottom: 'clamp(1.25rem, 3vh, 3rem)' }}
          >
            {/* Left — copy */}
            <div className="flex-1 max-w-2xl">
              {/* DeFi banner with checkered flag */}
              <div className="flex items-center gap-2 sm:gap-4 mb-2 sm:mb-3">
                <svg viewBox="0 0 36 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 w-[28px] h-[34px] sm:w-[36px] sm:h-[44px] lg:w-[44px] lg:h-[54px]">
                  <rect x="2" y="0" width="2.5" height="44" rx="1" fill="rgba(255,255,255,0.6)" />
                  <g transform="translate(4.5,1)">
                    <rect width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="7" y="0" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                    <rect x="14" y="0" width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="21" y="0" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                    <rect x="0" y="7" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                    <rect x="7" y="7" width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="14" y="7" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                    <rect x="21" y="7" width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="0" y="14" width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="7" y="14" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                    <rect x="14" y="14" width="7" height="7" fill="white" opacity="0.95" />
                    <rect x="21" y="14" width="7" height="7" fill="rgba(255,255,255,0.15)" />
                  </g>
                </svg>
                <div className="flex flex-col">
                  <span className="font-black text-white tracking-tight leading-none" style={{ fontSize: 'clamp(1.875rem, min(6vw, 7vh), 4.5rem)', letterSpacing: '-0.03em', textShadow: '0 0 40px rgba(255,255,255,0.15)' }}>
                    DeFi
                  </span>
                  <span className="font-bold text-purple-300/90 tracking-tight leading-tight" style={{ fontSize: 'clamp(1.0625rem, min(3.2vw, 3.8vh), 2.25rem)', letterSpacing: '-0.02em' }}>
                    Safe Harbor
                  </span>
                </div>
              </div>
              <div className="mb-2 sm:mb-3">
                <div className="text-sm sm:text-base font-bold tracking-wide text-white/90">KRO Brokers, Social Purpose Corporation Presents</div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-purple-500/10 border border-purple-400/20 text-purple-300 text-[0.6rem] sm:text-xs font-semibold tracking-widest uppercase mb-3 sm:mb-5">
                  ✦ 155-Year Heritage · Digitized · Sovereign by Design
              </div>
              <h1 className="font-bold leading-[1.08] mb-2 sm:mb-3" style={{ fontSize: 'clamp(1.75rem, min(4.8vw, 5.9vh), 3.5rem)' }}>
                <button
                  type="button"
                  onClick={() => setShowKroPopup(true)}
                  className="group inline-flex items-baseline gap-1 bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400 rounded"
                  style={{ font: 'inherit', color: 'inherit' }}
                  aria-label="What does KRO stand for?"
                  title="What does KRO stand for?"
                >
                  <span className="underline decoration-dotted decoration-purple-400/50 underline-offset-4 group-hover:decoration-purple-300">KRO&rsquo;s</span>
                  <i className="bi bi-info-circle" style={{ fontSize: '0.5em', opacity: 0.55, verticalAlign: 'super' }}></i>
                </button>{' '}operating system<br />
                <span className="bg-gradient-to-r from-purple-400 to-fuchsia-400 bg-clip-text text-transparent">for trust-based capital.</span>
              </h1>
              {showKroPopup && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="kro-acronym-title"
                  onClick={() => setShowKroPopup(false)}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 60,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '1rem', background: 'rgba(6, 4, 16, 0.72)', backdropFilter: 'blur(6px)',
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'relative', width: '100%', maxWidth: '30rem',
                      borderRadius: '1.25rem', overflow: 'hidden',
                      background: 'linear-gradient(160deg, rgba(30,18,54,0.98), rgba(15,10,28,0.98))',
                      border: '1px solid rgba(192,132,252,0.28)',
                      boxShadow: '0 30px 80px -20px rgba(124,58,237,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset',
                    }}
                  >
                    <div style={{ height: '4px', background: 'linear-gradient(90deg, #a855f7, #d946ef, #a855f7)' }} />
                    <button
                      type="button"
                      onClick={() => setShowKroPopup(false)}
                      aria-label="Close"
                      style={{
                        position: 'absolute', top: '0.85rem', right: '0.95rem',
                        background: 'transparent', border: 0, color: 'rgba(226,232,240,0.6)',
                        fontSize: '1.25rem', lineHeight: 1, cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-x-lg" style={{ fontSize: '1rem' }}></i>
                    </button>
                    <div style={{ padding: '1.6rem 1.75rem 1.9rem' }}>
                      <div className="text-purple-300" style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                        The meaning behind the name
                      </div>
                      <h2 id="kro-acronym-title" className="text-white" style={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: '1.15rem' }}>
                        K·R·O
                      </h2>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                        {[
                          ['K', 'Knowledge', 'Institutional memory and verifiable truth, brought on-chain.'],
                          ['R', 'Reciprocity', 'Value exchanged fairly — equal treatment by design.'],
                          ['O', 'Ownership', 'Sovereign, compliant control of assets and data.'],
                        ].map(([letter, word, desc]) => (
                          <li key={letter} style={{ display: 'flex', gap: '0.9rem', alignItems: 'flex-start' }}>
                            <span
                              style={{
                                flexShrink: 0, width: '2.1rem', height: '2.1rem', borderRadius: '0.6rem',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 800, fontSize: '1.05rem', color: '#fff',
                                background: 'linear-gradient(135deg, rgba(168,85,247,0.35), rgba(217,70,239,0.35))',
                                border: '1px solid rgba(192,132,252,0.4)',
                              }}
                            >
                              {letter}
                            </span>
                            <div>
                              <div className="text-white" style={{ fontWeight: 700, fontSize: '0.98rem' }}>{word}</div>
                              <div className="text-slate-300/85" style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>{desc}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {showB2allPopup && (
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="b2all-title"
                  onClick={() => setShowB2allPopup(false)}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 60,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '1rem', background: 'rgba(6, 4, 16, 0.72)', backdropFilter: 'blur(6px)',
                  }}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'relative', width: '100%', maxWidth: '34rem',
                      borderRadius: '1.25rem', overflow: 'hidden',
                      background: 'linear-gradient(160deg, rgba(30,18,54,0.98), rgba(15,10,28,0.98))',
                      border: '1px solid rgba(192,132,252,0.28)',
                      boxShadow: '0 30px 80px -20px rgba(124,58,237,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset',
                    }}
                  >
                    <div style={{ height: '4px', background: 'linear-gradient(90deg, #a855f7, #d946ef, #a855f7)' }} />
                    <button
                      type="button"
                      onClick={() => setShowB2allPopup(false)}
                      aria-label="Close"
                      style={{
                        position: 'absolute', top: '0.85rem', right: '0.95rem',
                        background: 'transparent', border: 0, color: 'rgba(226,232,240,0.6)',
                        fontSize: '1.25rem', lineHeight: 1, cursor: 'pointer',
                      }}
                    >
                      <i className="bi bi-x-lg" style={{ fontSize: '1rem' }}></i>
                    </button>
                    <div style={{ padding: '1.6rem 1.75rem 1.9rem' }}>
                      <div className="text-purple-300" style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                        OS Identity · B2ALL
                      </div>
                      <h2 id="b2all-title" className="text-white" style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: '1.15rem', lineHeight: 1.25 }}>
                        Business to All. One operating system, five doors.
                      </h2>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                        {[
                          ['Buyers', 'A protected, compliant lane for real-world value', 'enterprise-governance', 'bi-shield-check'],
                          ['Investors', 'A patent-pending moat in the $40T ESG economy', 'https://krotrust.org/investor-relations', 'bi-graph-up-arrow'],
                          ['Civic Partners', '155 years of stewardship, now on-chain', 'https://krotrust.org/social-purpose', 'bi-bank'],
                          ['Operators', 'Live rails: T+0 Canton DvP, MPC custody, CIP-0056', 'settlement-hub', 'bi-lightning-charge'],
                          ['Distribution', 'Bring your network into the corridor', 'https://www.kromarketplace.com/register-trial', 'bi-diagram-3'],
                        ].map(([role, desc, target, icon]) => (
                          <li key={role}>
                            <button
                              type="button"
                              onClick={() => {
                                if (target.startsWith('http')) {
                                  setShowB2allPopup(false);
                                  window.open(target, '_blank', 'noopener');
                                } else {
                                  scrollToSection(target);
                                }
                              }}
                              className="group"
                              style={{
                                width: '100%', textAlign: 'left', cursor: 'pointer',
                                display: 'flex', gap: '0.9rem', alignItems: 'center',
                                padding: '0.85rem 1rem', borderRadius: '0.75rem',
                                background: 'rgba(168,85,247,0.06)',
                                border: '1px solid rgba(192,132,252,0.18)',
                                color: 'inherit', transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(168,85,247,0.16)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.4)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(168,85,247,0.06)'; e.currentTarget.style.borderColor = 'rgba(192,132,252,0.18)'; }}
                            >
                              <span
                                style={{
                                  flexShrink: 0, width: '2.1rem', height: '2.1rem', borderRadius: '0.6rem',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: 'linear-gradient(135deg, rgba(168,85,247,0.35), rgba(217,70,239,0.35))',
                                  border: '1px solid rgba(192,132,252,0.4)',
                                }}
                              >
                                <i className={`bi ${icon} text-white`} style={{ fontSize: '0.95rem' }}></i>
                              </span>
                              <div style={{ flex: 1 }}>
                                <div className="text-white" style={{ fontWeight: 700, fontSize: '0.95rem' }}>{role}</div>
                                <div className="text-slate-300/85" style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>{desc}</div>
                              </div>
                              <i className="bi bi-arrow-right text-purple-300/60 group-hover:text-purple-200" style={{ fontSize: '0.85rem' }}></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <div className="relative pl-4 sm:pl-5 mb-3 sm:mb-5 max-w-[54ch]">
                <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-500 to-purple-600" />
                <p className="text-white font-semibold m-0" style={{ fontSize: 'clamp(0.95rem, min(2.1vw, 2.5vh), 1.5rem)', lineHeight: 1.38, letterSpacing: '-0.01em' }}>
                  Welcome to a new class of secure, frictionless, and real-world payment exchange.{' '}
                  <span className="bg-gradient-to-r from-purple-300 to-fuchsia-300 bg-clip-text text-transparent">
                    Equal and deterministic treatment awaits.
                  </span>
                </p>
              </div>
              <p className="text-slate-300/90 leading-relaxed mb-4 sm:mb-6 max-w-[56ch]" style={{ fontSize: 'clamp(0.8rem, min(1.45vw, 1.8vh), 1.15rem)', lineHeight: 1.6 }}>
                <span className="text-white font-bold">How it works: Reciprocity.</span>{' '}
                You invest in us, and we invest in you. KRO records your work, guarantees your payouts, and secures your assets in our stable <strong className="text-white font-bold">AI Safe Harbor Studio</strong>. We handle the complex mechanics so you don’t have to. Hope is here.
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6">
                <button 
                  onClick={handleLaunch} 
                  className="lp-cta-shimmer px-5 sm:px-7 rounded-xl bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-600 text-xs sm:text-sm font-bold shadow-lg shadow-purple-600/25 hover:shadow-purple-500/40 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all w-full sm:w-auto"
                  style={{ height: '2.75rem' }}
                >
                  {getButtonText()}
                </button>
                <a href="https://www.kromarketplace.com/" target="_blank" rel="noopener noreferrer"
                  className="px-5 sm:px-7 rounded-xl border border-white/15 text-xs sm:text-sm font-medium flex items-center gap-2 hover:bg-white/5 hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all w-full sm:w-auto justify-center"
                  style={{ height: '2.75rem' }}
                >
                  SPC Portal
                  <i className="bi bi-arrow-right" style={{ fontSize: '0.85rem', opacity: 0.7 }}></i>
                </a>
              </div>
              {/* Mobile motto + rocket section */}
              <div className="flex lg:hidden flex-col items-center text-center mt-4 mb-6">
                {/* Scaled-down rocket for mobile */}
                <div className="relative mb-4">
                  <div className="absolute rounded-full" style={{ width: '140px', height: '140px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)', filter: 'blur(15px)', pointerEvents: 'none' }} />
                  <svg width="100" height="120" viewBox="0 0 180 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10" style={{ filter: 'drop-shadow(0 0 20px rgba(139,92,246,0.3))' }}>
                    <ellipse cx="90" cy="195" rx="18" ry="22" fill="url(#flameGradM)" opacity="0.8">
                      <animate attributeName="ry" values="22;26;22" dur="0.8s" repeatCount="indefinite" />
                    </ellipse>
                    <ellipse cx="90" cy="190" rx="10" ry="14" fill="url(#flameCoreGradM)" opacity="0.9">
                      <animate attributeName="ry" values="14;17;14" dur="0.6s" repeatCount="indefinite" />
                    </ellipse>
                    <path d="M90 12 C90 12 60 60 60 120 L60 160 C60 168 68 175 90 175 C112 175 120 168 120 160 L120 120 C120 60 90 12 90 12Z" fill="url(#bodyGradM)" stroke="rgba(192,132,252,0.4)" strokeWidth="1.5" />
                    <circle cx="90" cy="90" r="16" fill="#0c0a15" stroke="url(#windowStrokeM)" strokeWidth="2" />
                    <circle cx="90" cy="90" r="11" fill="url(#windowGradM)" />
                    <circle cx="85" cy="86" r="3" fill="rgba(255,255,255,0.25)" />
                    <path d="M60 135 L38 170 L60 165Z" fill="#7c3aed" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />
                    <path d="M120 135 L142 170 L120 165Z" fill="#7c3aed" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />
                    <defs>
                      <linearGradient id="bodyGradM" x1="90" y1="12" x2="90" y2="175" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#c084fc" /><stop offset="35%" stopColor="#8b5cf6" /><stop offset="100%" stopColor="#4c1d95" />
                      </linearGradient>
                      <linearGradient id="windowStrokeM" x1="74" y1="74" x2="106" y2="106" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#6d28d9" />
                      </linearGradient>
                      <radialGradient id="windowGradM" cx="50%" cy="40%">
                        <stop offset="0%" stopColor="#1e1b4b" /><stop offset="100%" stopColor="#06020f" />
                      </radialGradient>
                      <radialGradient id="flameGradM" cx="50%" cy="30%">
                        <stop offset="0%" stopColor="#fbbf24" /><stop offset="50%" stopColor="#f97316" /><stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
                      </radialGradient>
                      <radialGradient id="flameCoreGradM" cx="50%" cy="30%">
                        <stop offset="0%" stopColor="#fef3c7" /><stop offset="100%" stopColor="#fbbf24" stopOpacity="0.3" />
                      </radialGradient>
                    </defs>
                  </svg>
                </div>
                <div className="text-xl font-black tracking-tight bg-gradient-to-r from-purple-300 via-fuchsia-300 to-purple-400 bg-clip-text text-transparent">
                  Ten Toes Down
                </div>
                <div className="text-xl font-black tracking-tight bg-gradient-to-r from-purple-300 via-fuchsia-300 to-purple-400 bg-clip-text text-transparent">
                  Governance Framework
                </div>
                <div className="mt-1.5 text-[0.6rem] uppercase tracking-[0.2em] text-white font-semibold">
                  Trust · Loyalty · Upward Mobility
                </div>
                <p className="mt-2 text-[0.75rem] text-purple-300/80 italic font-bold leading-relaxed max-w-[280px]">
                  Powered by institutional grade governance and decentralized infrastructure.
                </p>
                <div className="mt-3 flex flex-col items-center gap-1.5">
                  <span className="text-[0.6rem] text-slate-400 font-medium tracking-wide uppercase">Settlement Hub Portal</span>
                  <a href="https://www.kromarketplace.com/ttd-portal?page=portfolio" target="_blank" rel="noopener noreferrer" className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-slate-300 text-xs font-semibold tracking-wide hover:bg-white/[0.08] transition-all">KRO Exchange</a>
                </div>
              </div>
            </div>

            {/* Right — "Ten Toes Down" motto + galactic rocket (desktop) */}
            <div className="hidden lg:flex flex-col items-center justify-center flex-shrink-0" style={{ width: '340px' }}>
              <div className="relative flex flex-col items-center">
                {/* Galactic glow ring */}
                <div className="absolute rounded-full" style={{ width: '280px', height: '280px', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, rgba(99,102,241,0.08) 40%, transparent 70%)', filter: 'blur(30px)', pointerEvents: 'none' }} />

                {/* Rocket ship SVG */}
                <svg width="180" height="220" viewBox="0 0 180 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10" style={{ filter: 'drop-shadow(0 0 30px rgba(139,92,246,0.4))' }}>
                  {/* Exhaust flame */}
                  <ellipse cx="90" cy="195" rx="18" ry="22" fill="url(#flameGrad)" opacity="0.8">
                    <animate attributeName="ry" values="22;26;22" dur="0.8s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.8;0.5;0.8" dur="0.8s" repeatCount="indefinite" />
                  </ellipse>
                  <ellipse cx="90" cy="190" rx="10" ry="14" fill="url(#flameCoreGrad)" opacity="0.9">
                    <animate attributeName="ry" values="14;17;14" dur="0.6s" repeatCount="indefinite" />
                  </ellipse>

                  {/* Rocket body */}
                  <path d="M90 12 C90 12 60 60 60 120 L60 160 C60 168 68 175 90 175 C112 175 120 168 120 160 L120 120 C120 60 90 12 90 12Z" fill="url(#bodyGrad)" stroke="url(#bodyStroke)" strokeWidth="1.5" />

                  {/* Nose cone highlight */}
                  <path d="M90 18 C85 40 75 70 75 110 L80 110 C82 70 87 42 90 22Z" fill="rgba(255,255,255,0.08)" />

                  {/* Window / porthole */}
                  <circle cx="90" cy="90" r="16" fill="#0c0a15" stroke="url(#windowStroke)" strokeWidth="2" />
                  <circle cx="90" cy="90" r="11" fill="url(#windowGrad)" />
                  <circle cx="85" cy="86" r="3" fill="rgba(255,255,255,0.25)" />

                  {/* Left fin */}
                  <path d="M60 135 L38 170 L60 165Z" fill="url(#finGrad)" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />
                  {/* Right fin */}
                  <path d="M120 135 L142 170 L120 165Z" fill="url(#finGrad)" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />

                  {/* Star particles */}
                  {[[30,40],[150,55],[25,130],[155,120],[45,180],[135,185],[20,80],[160,90]].map(([cx,cy],i) => (
                    <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 1.5 : 1} fill="white" opacity={0.3 + (i % 3) * 0.2}>
                      <animate attributeName="opacity" values={`${0.2 + (i%3)*0.2};${0.6 + (i%2)*0.2};${0.2 + (i%3)*0.2}`} dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
                    </circle>
                  ))}

                  {/* Gradients */}
                  <defs>
                    <linearGradient id="bodyGrad" x1="90" y1="12" x2="90" y2="175" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#c084fc" />
                      <stop offset="35%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#4c1d95" />
                    </linearGradient>
                    <linearGradient id="bodyStroke" x1="60" y1="12" x2="120" y2="175" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="rgba(192,132,252,0.6)" />
                      <stop offset="100%" stopColor="rgba(76,29,149,0.3)" />
                    </linearGradient>
                    <linearGradient id="finGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" />
                      <stop offset="100%" stopColor="#4c1d95" />
                    </linearGradient>
                    <radialGradient id="windowGrad" cx="50%" cy="40%">
                      <stop offset="0%" stopColor="#1e1b4b" />
                      <stop offset="60%" stopColor="#0f0a2a" />
                      <stop offset="100%" stopColor="#06020f" />
                    </radialGradient>
                    <linearGradient id="windowStroke" x1="74" y1="74" x2="106" y2="106" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#a78bfa" />
                      <stop offset="100%" stopColor="#6d28d9" />
                    </linearGradient>
                    <radialGradient id="flameGrad" cx="50%" cy="30%">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="50%" stopColor="#f97316" />
                      <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="flameCoreGrad" cx="50%" cy="30%">
                      <stop offset="0%" stopColor="#fef3c7" />
                      <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.3" />
                    </radialGradient>
                  </defs>
                </svg>

                {/* Motto */}
                <div className="relative z-10 mt-6 text-center">
                  <div className="text-3xl font-black tracking-tight bg-gradient-to-r from-purple-300 via-fuchsia-300 to-purple-400 bg-clip-text text-transparent" style={{ letterSpacing: '0.02em' }}>
                    Ten Toes Down
                  </div>
                  <div className="text-3xl font-black tracking-tight bg-gradient-to-r from-purple-300 via-fuchsia-300 to-purple-400 bg-clip-text text-transparent" style={{ letterSpacing: '0.02em' }}>
                    Governance Framework
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.25em] text-white font-semibold">
                    Trust · Loyalty · Upward Mobility
                  </div>
                  <p className="mt-3 text-sm text-purple-300/80 italic font-bold leading-relaxed text-center max-w-[320px]">
                    Powered by institutional grade governance and decentralized infrastructure.
                  </p>
                  <div className="mt-4 flex flex-col items-center gap-2">
                    <span className="text-xs text-slate-400 font-medium tracking-wide uppercase">Settlement Hub Portal</span>
                    <a href="https://www.kromarketplace.com/ttd-portal?page=portfolio" target="_blank" rel="noopener noreferrer" className="px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-slate-300 text-sm font-semibold tracking-wide hover:bg-white/[0.08] hover:border-purple-400/30 transition-all">KRO Exchange</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signal Strip */}
      <div className="lp-section-divider" />
      <section id="settlement-hub" className="py-10 sm:py-14 scroll-mt-24" style={{ background: 'linear-gradient(90deg, rgba(99,102,241,0.04), rgba(139,92,246,0.03), rgba(99,102,241,0.04))' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 mb-2.5">
              <i className="bi bi-shield-check text-purple-400/70" style={{ fontSize: '0.85rem' }}></i>
              <span className="text-slate-400 text-[0.6rem] sm:text-[0.7rem] uppercase tracking-[0.22em] font-semibold">Settlement Infrastructure</span>
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">Regulated On-Chain Layer</h3>
            <p className="text-slate-300/90 text-xs sm:text-sm font-semibold">KRO owns the utility. Network Agnostic Design.</p>
            <div className="w-12 sm:w-16 h-[2px] bg-gradient-to-r from-purple-500 to-fuchsia-500 mx-auto rounded-full mt-4 sm:mt-5"></div>
          </div>

          {/* Specs — one contained cell each so all four stay scannable */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4" aria-label="Settlement infrastructure specifications">
            {[
              { label: 'RWA Staking & Settlement', value: 'T+0 DvP, Provenance & Canton', icon: 'bi-lightning-charge-fill' },
              { label: 'Markers', value: '3 Active on pio-mainnet-1', icon: 'bi-diagram-3-fill' },
              { label: 'Custody', value: 'BitGo MPC 2-of-3', icon: 'bi-lock-fill' },
              { label: 'Standard', value: 'Deterministic CIP‑0056', icon: 'bi-file-earmark-check-fill' },
            ].map((s) => (
              <div
                key={s.label}
                className="group rounded-xl sm:rounded-2xl border border-white/[0.07] bg-white/[0.025] px-3 py-3.5 sm:px-4 sm:py-4 transition-all hover:border-purple-400/30 hover:bg-white/[0.05]"
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex items-center justify-center rounded-lg bg-purple-500/10 border border-purple-400/20 transition-colors group-hover:bg-purple-500/20" style={{ width: '1.5rem', height: '1.5rem' }}>
                    <i className={`bi ${s.icon} text-purple-300`} style={{ fontSize: '0.7rem' }}></i>
                  </span>
                  <span className="text-slate-400 text-[0.58rem] sm:text-[0.64rem] uppercase tracking-[0.12em] font-bold leading-tight">{s.label}</span>
                </div>
                <div className="text-white text-[0.78rem] sm:text-sm font-semibold leading-snug">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Outcome — the payoff, not a spec, so it gets its own emphasis */}
          <div className="mt-2.5 sm:mt-4 rounded-xl sm:rounded-2xl border border-purple-400/20 bg-gradient-to-r from-purple-500/[0.07] via-fuchsia-500/[0.05] to-purple-500/[0.07] px-4 py-3.5 sm:px-6 sm:py-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3.5 text-center sm:text-left">
              <span className="flex items-center justify-center rounded-lg bg-purple-500/15 border border-purple-400/25 shrink-0" style={{ width: '1.75rem', height: '1.75rem' }}>
                <i className="bi bi-patch-check-fill text-purple-300" style={{ fontSize: '0.85rem' }}></i>
              </span>
              <span className="text-slate-400 text-[0.58rem] sm:text-[0.64rem] uppercase tracking-[0.12em] font-bold shrink-0">Outcome</span>
              <span className="text-white text-[0.8rem] sm:text-sm font-semibold leading-snug">
                Verification confidence that makes capital bankable and outcomes accountable.
              </span>
            </div>
          </div>

          {/* What "Deterministic" means — branching outcome design */}
          <div className="mt-8 sm:mt-10 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="bi bi-diagram-2-fill text-purple-400/70" style={{ fontSize: '0.85rem' }}></i>
              <span className="text-slate-400 text-[0.6rem] sm:text-[0.7rem] uppercase tracking-[0.22em] font-semibold">What “Deterministic” Means</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Proceeds */}
              <div className="relative rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] px-4 py-4 sm:px-6 sm:py-5 overflow-hidden transition-all hover:border-emerald-400/50 hover:bg-emerald-500/[0.1]">
                <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-emerald-400 to-emerald-600" />
                <div className="flex items-center gap-2 mb-2.5">
                  <i className="bi bi-check-circle-fill text-emerald-400" style={{ fontSize: '1.05rem' }}></i>
                  <span className="text-emerald-300 text-xs sm:text-sm font-bold uppercase tracking-wide">Conditions met</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm sm:text-base font-semibold">
                  <i className="bi bi-arrow-right-short text-emerald-400" style={{ fontSize: '1.25rem' }}></i>
                  <span>Settlement proceeds.</span>
                </div>
              </div>
              {/* Does not */}
              <div className="relative rounded-2xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-4 sm:px-6 sm:py-5 overflow-hidden transition-all hover:border-rose-400/50 hover:bg-rose-500/[0.1]">
                <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-rose-400 to-rose-600" />
                <div className="flex items-center gap-2 mb-2.5">
                  <i className="bi bi-x-circle-fill text-rose-400" style={{ fontSize: '1.05rem' }}></i>
                  <span className="text-rose-300 text-xs sm:text-sm font-bold uppercase tracking-wide">Conditions not met</span>
                </div>
                <div className="flex items-center gap-2 text-white text-sm sm:text-base font-semibold">
                  <i className="bi bi-arrow-right-short text-rose-400" style={{ fontSize: '1.25rem' }}></i>
                  <span>It does not.</span>
                </div>
              </div>
            </div>
            <p className="text-center text-slate-500 text-[0.62rem] sm:text-[0.7rem] mt-3.5 italic">
              No discretion. No overrides. Enforced in code, the same way every time.
            </p>
          </div>
        </div>
      </section>
      <div className="lp-section-divider" />

      {/* Intelligence Layer */}
      <section className="relative py-14 sm:py-24 md:py-28 bg-[#0c0a15] z-10" id="pillars">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="why-it-matters" className="text-center mb-10 sm:mb-16 scroll-mt-24" data-reveal>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-white">
              Why Determinism Matters
            </h2>
            <p className="text-slate-300/90 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed px-2 sm:px-0"><span className="text-white font-semibold">Rocket Science Unlocked:</span> We built a protected moat for real-world value and embedded it into the projected $40T ESG Economy. Our next-gen infrastructure seamlessly integrates supply, demand, and settlement records, making them exchangeable for instant payment transactions.</p>
            <div className="w-12 sm:w-16 h-[2px] bg-gradient-to-r from-purple-500 to-fuchsia-500 mx-auto rounded-full mt-4 sm:mt-5"></div>
          </div>
          <div data-reveal className="max-w-3xl mx-auto mb-10 sm:mb-16">
            <button
              type="button"
              onClick={() => setShowSollomonPopup(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-purple-500/[0.06] border border-purple-400/20 px-5 py-4 sm:px-6 sm:py-5 text-white font-semibold text-sm sm:text-base hover:bg-purple-500/[0.12] hover:border-purple-400/40 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
              aria-haspopup="dialog"
              title="Read The Sollomon® Benchmark"
            >
              <span>💎 The Sollomon® Benchmark</span>
              <i className="bi bi-arrow-up-right-circle" style={{ fontSize: '0.85rem', opacity: 0.6 }}></i>
            </button>
          </div>
          {showSollomonPopup && (
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="sollomon-title"
              onClick={() => setShowSollomonPopup(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 60,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem', background: 'rgba(6, 4, 16, 0.72)', backdropFilter: 'blur(6px)',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'relative', width: '100%', maxWidth: '42rem',
                  borderRadius: '1.25rem', overflow: 'hidden',
                  background: 'linear-gradient(160deg, rgba(30,18,54,0.98), rgba(15,10,28,0.98))',
                  border: '1px solid rgba(192,132,252,0.28)',
                  boxShadow: '0 30px 80px -20px rgba(124,58,237,0.55), 0 0 0 1px rgba(255,255,255,0.03) inset',
                }}
              >
                <div style={{ height: '4px', background: 'linear-gradient(90deg, #a855f7, #d946ef, #a855f7)' }} />
                <button
                  type="button"
                  onClick={() => setShowSollomonPopup(false)}
                  aria-label="Close"
                  style={{
                    position: 'absolute', top: '0.85rem', right: '0.95rem',
                    background: 'transparent', border: 0, color: 'rgba(226,232,240,0.6)',
                    fontSize: '1.25rem', lineHeight: 1, cursor: 'pointer',
                  }}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: '1rem' }}></i>
                </button>
                <div style={{ padding: '2rem 2.25rem 2.35rem' }}>
                  <div className="text-purple-300" style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                    Independent Verification
                  </div>
                  <h2 id="sollomon-title" className="text-white" style={{ fontSize: '1.7rem', fontWeight: 800, letterSpacing: '-0.01em', marginBottom: '1.25rem', lineHeight: 1.2 }}>
                    💎 The Sollomon® Benchmark
                  </h2>
                  <p className="text-slate-200" style={{ fontSize: '1.02rem', lineHeight: 1.75, margin: 0 }}>
                    Our underlying operating system has been independently verified through a formal UK IP Valuation. Evaluated on a lending-grade, relief-from-royalty framework, our core architecture meets the underwriteable asset quality standards required by internationally rigorous banking institutions.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="mb-6 sm:mb-8" data-reveal>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">Intelligence Layer</h2>
            <p className="text-slate-300 text-sm">KROs Premium value layer</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {pillars.map(p => (
              <div key={p.title} data-reveal className="reveal-init lp-card group rounded-xl bg-white/[0.025] border border-white/[0.06] p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 hover:bg-white/[0.05] hover:border-purple-500/20">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/15 flex items-center justify-center lp-pillar-icon">
                  <i className={`bi ${p.icon} text-purple-400`} style={{ fontSize: '1.1rem' }}></i>
                </div>
                <h3 className="text-sm font-semibold tracking-wide text-white">{p.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed flex-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Module Grid */}
      <section id="modules" className="py-14 sm:py-24 md:py-28" style={{ background: 'linear-gradient(180deg, #0c0a15, #0e0b18 30%, #0e0b18 70%, #0c0a15)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div id="governance-registry" className="flex items-end justify-between mb-8 sm:mb-12 scroll-mt-24" data-reveal>
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">Platform Modules</h2>
              <p className="text-slate-300 text-sm">Explore the building blocks of Trust Tech Studio.</p>
            </div>
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider hidden sm:block">Sample View</span>
          </div>
          <div data-reveal className="reveal-init">
            <LandingModules modules={modules} />
          </div>
        </div>
      </section>

      {/* Report Teaser */}
      <div className="lp-section-divider" />
      <section id="registry-participation" className="py-14 sm:py-24 md:py-28 reveal-init scroll-mt-24" data-reveal style={{ background: 'linear-gradient(180deg, #0c0a15, #0a0814)' }}>
        <div className="max-w-lg mx-auto px-4 sm:px-6 text-center">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-500/10 border border-purple-500/15 flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <i className="bi bi-file-earmark-bar-graph text-purple-400" style={{ fontSize: '1.2rem' }}></i>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-white">Get the Integrity Benchmark</h2>
          <p className="text-slate-300 text-sm mb-6 sm:mb-10 leading-relaxed">See how trust signals convert into measurable market confidence.</p>
          {benchmarkStatus === 'success' ? (
            <div className="max-w-sm mx-auto rounded-xl border border-emerald-400/30 bg-emerald-500/[0.08] px-5 py-5 flex items-start gap-3 text-left">
              <i className="bi bi-check-circle-fill text-emerald-400 flex-shrink-0" style={{ fontSize: '1.25rem' }}></i>
              <div>
                <p className="text-white font-semibold text-sm mb-1">Request received — thank you!</p>
                <p className="text-slate-300 text-xs leading-relaxed">We've logged your request for the Integrity Benchmark. Someone from our team will reach out within 24–48 hours.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleBenchmarkSubmit} className="max-w-sm mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={benchmarkEmail}
                onChange={(e) => setBenchmarkEmail(e.target.value)}
                placeholder="Work email"
                className="flex-1 h-11 sm:h-12 rounded-xl bg-white/[0.04] border border-white/[0.08] px-4 sm:px-5 text-xs sm:text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500/30 transition-all"
              />
              <button
                type="submit"
                disabled={benchmarkStatus === 'submitting'}
                className="h-11 sm:h-12 px-5 sm:px-6 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 text-xs sm:text-sm font-semibold hover:from-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all shadow-lg shadow-purple-600/20 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {benchmarkStatus === 'submitting' ? 'Sending…' : 'Get Sample'}
              </button>
            </form>
          )}
          {benchmarkStatus === 'error' && (
            <p className="text-rose-300 text-xs mt-3 max-w-sm mx-auto">
              Something went wrong sending your request. Please email <a href="mailto:stewards@kromarketplace.com" className="underline hover:text-rose-200">stewards@kromarketplace.com</a> and we'll get you the report.
            </p>
          )}
        </div>
      </section>

      {/* Integrations */}
      <div className="lp-section-divider" />
      <section id="enterprise-governance" className="py-14 sm:py-20 md:py-24 bg-[#0c0a15] scroll-mt-24">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12" data-reveal>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2">Enterprise Compatible</h2>
            <p className="text-slate-300 text-sm">Connects with the tools your team already uses.</p>
          </div>
          <div data-reveal className="reveal-init">
            <LandingIntegrations items={['Slack','Teams','ServiceNow','Snowflake','Azure','Canton Network']} />
          </div>
        </div>
      </section>

      <div className="lp-section-divider" />
    </div>
  );
}
